import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AppProvider } from './contexts/AppContext';
import LoginPage from './pages/LoginPage';
import StudentDashboard from './pages/StudentDashboard';
import StudentHomeworkPage from './pages/StudentHomeworkPage';
import Dashboard from './pages/Dashboard';
import StudentsPage from './pages/StudentsPage';
import ClassesPage from './pages/ClassesPage';
import ClassDetailPage from './pages/ClassDetailPage';
import AttendancePage from './pages/AttendancePage';
import HomeworkPage from './pages/HomeworkPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import StudentProfilePage from './pages/StudentProfilePage';
import MyProfilePage from './pages/MyProfilePage';
import LoadingSpinner from './components/LoadingSpinner';

const ProtectedRoute: React.FC<{ children: React.ReactNode; requiredRole?: 'teacher' | 'student' }> = ({ 
  children, 
  requiredRole 
}) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole && user.role !== requiredRole) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};

const RoleBasedRedirect: React.FC = () => {
  const { user } = useAuth();
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role === 'student') {
    return <Navigate to="/student-dashboard" replace />;
  }

  return <Navigate to="/dashboard" replace />;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={<RoleBasedRedirect />} />
      
      {/* Student Routes */}
      <Route path="/student-dashboard" element={
        <ProtectedRoute requiredRole="student">
          <StudentDashboard />
        </ProtectedRoute>
      } />
      <Route path="/student-homework" element={
        <ProtectedRoute requiredRole="student">
          <StudentHomeworkPage />
        </ProtectedRoute>
      } />
      <Route path="/student-profile" element={
        <ProtectedRoute requiredRole="student">
          <MyProfilePage />
        </ProtectedRoute>
      } />
      
      {/* Teacher Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute requiredRole="teacher">
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/students" element={
        <ProtectedRoute requiredRole="teacher">
          <StudentsPage />
        </ProtectedRoute>
      } />
      <Route path="/students/:id" element={
        <ProtectedRoute requiredRole="teacher">
          <StudentProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/classes" element={
        <ProtectedRoute requiredRole="teacher">
          <ClassesPage />
        </ProtectedRoute>
      } />
      <Route path="/classes/:classId" element={
        <ProtectedRoute requiredRole="teacher">
          <ClassDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/attendance" element={
        <ProtectedRoute requiredRole="teacher">
          <AttendancePage />
        </ProtectedRoute>
      } />
      <Route path="/homework" element={
        <ProtectedRoute requiredRole="teacher">
          <HomeworkPage />
        </ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute requiredRole="teacher">
          <ReportsPage />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute requiredRole="teacher">
          <SettingsPage />
        </ProtectedRoute>
      } />
    </Routes>
  );
};

const App: React.FC = () => {
  // Initialize theme on app load
  useEffect(() => {
    const initializeTheme = () => {
      const savedSettings = localStorage.getItem('teacher-tracker-settings');
      if (savedSettings) {
        try {
          const parsed = JSON.parse(savedSettings);
          const theme = parsed.appearance?.theme || 'light';
          const root = document.documentElement;
          
          if (theme === 'dark') {
            root.classList.add('dark');
          } else if (theme === 'light') {
            root.classList.remove('dark');
          } else if (theme === 'auto') {
            // Auto theme based on system preference
            if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
              root.classList.add('dark');
            } else {
              root.classList.remove('dark');
            }
          }
        } catch (error) {
          console.error('Failed to load theme settings:', error);
        }
      }
    };

    initializeTheme();

    // Listen for system theme changes when auto is selected
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = () => {
      const savedSettings = localStorage.getItem('teacher-tracker-settings');
      if (savedSettings) {
        try {
          const parsed = JSON.parse(savedSettings);
          if (parsed.appearance?.theme === 'auto') {
            const root = document.documentElement;
            if (mediaQuery.matches) {
              root.classList.add('dark');
            } else {
              root.classList.remove('dark');
            }
          }
        } catch (error) {
          console.error('Failed to handle system theme change:', error);
        }
      }
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, []);

  return (
    <AuthProvider>
      <AppProvider>
        <Router>
          <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <AppRoutes />
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  duration: 3000,
                },
                error: {
                  duration: 5000,
                },
              }}
            />
          </div>
        </Router>
      </AppProvider>
    </AuthProvider>
  );
};

export default App;
